/**
 * Kâr tablosu hesap motoru.
 *
 * ⚠️ Bu dosya KSK Panel'den **birebir** taşındı; oradaki mantık da masaüstü
 * programın (`calculate_row_data`) birebir karşılığıydı. Formülü "iyileştirmek"
 * yasak: kullanıcının geçmiş yıllara ait tabloları bu hesaba göre tutuluyor ve
 * en ufak sapma, kapanmış ayların kârını değiştirir.
 *
 * Hem ekran (canlı önizleme) hem sunucu (kayıt) buradan hesaplıyor — iki ayrı
 * kopya olsaydı ekranda gördüğünüz kâr ile kaydedilen kâr birbirini tutmazdı.
 */

/** Bir satırın kullanıcı tarafından girilen alanları. */
export interface KarSatirGirdi {
  /** Amazon'dan gelen tahsilat (VAT hariç). */
  gelir: number;
  /** Amazon komisyonu. */
  amzFee: number;
  /** Promosyon / indirim. */
  promo: number;
  /** Ürünün alış bedeli. */
  buyFee: number;
  /** Depo ücreti. */
  depo: number;
  /** İade gideri. */
  iade: number;
  /** Diğer giderler. */
  other: number;
}

/** Yüzde olarak oranlar. */
export interface KarOranlari {
  /** Kredi kartı komisyonu — alış + depo üzerinden. */
  kkRate: number;
  /** Para çekme komisyonu — net tahsilat üzerinden. */
  pCekmeRate: number;
  /** Amazon çeviri komisyonu. */
  amzCeviriRate: number;
  /** Kur çeviri komisyonu. */
  kurCeviriRate: number;
  /** Faiz gideri — alış + depo üzerinden. */
  faizRate: number;
}

export const VARSAYILAN_ORANLAR: KarOranlari = {
  kkRate: 0,
  pCekmeRate: 2,
  amzCeviriRate: 1,
  kurCeviriRate: 1,
  faizRate: 10,
};

export interface KarSatirSonuc {
  kk: number;
  pCekme: number;
  amzCeviri: number;
  kurCeviri: number;
  faiz: number;
  toplamGider: number;
  kar: number;
  roi: number;
}

/**
 * Bir satırın türetilmiş alanlarını hesaplar.
 *
 * Formül (değiştirilmedi):
 *   temelKomisyon = gelir − amzFee − promo − iade − other
 *   temelMaliyet  = buyFee + depo
 *
 *   kk         = temelMaliyet  × kkRate%
 *   pCekme     = temelKomisyon × pCekmeRate%
 *   amzCeviri  = temelKomisyon × amzCeviriRate%
 *   kurCeviri  = temelKomisyon × kurCeviriRate%
 *   faiz       = temelMaliyet  × faizRate%
 *
 *   toplamGider = buyFee + depo + iade + other + kk + pCekme + amzCeviri
 *                 + kurCeviri + faiz
 *   kar = (gelir − amzFee − promo) − toplamGider
 *   roi = toplamGider > 0 ? kar ÷ toplamGider × 100 : 0
 *
 * ⚠️ `kar` hesabında iade ve other **iki kez** görünüyor gibi durabilir: bir kez
 * `temelKomisyon`'da düşülüyor, bir kez `toplamGider`'de toplanıyor. Bu bilinçli
 * — birincisi komisyon matrahını küçültüyor, ikincisi gideri sayıyor. Orijinal
 * hesap böyle; düzeltmeye kalkışmak geçmiş ayları bozar.
 */
export function karSatiriHesapla(girdi: KarSatirGirdi, oran: KarOranlari): KarSatirSonuc {
  const { gelir, amzFee, promo, buyFee, depo, iade, other } = girdi;

  const temelKomisyon = gelir - amzFee - promo - iade - other;
  const temelMaliyet = buyFee + depo;

  const kk = temelMaliyet * (oran.kkRate / 100);
  const pCekme = temelKomisyon * (oran.pCekmeRate / 100);
  const amzCeviri = temelKomisyon * (oran.amzCeviriRate / 100);
  const kurCeviri = temelKomisyon * (oran.kurCeviriRate / 100);
  const faiz = temelMaliyet * (oran.faizRate / 100);

  const toplamGider = buyFee + depo + iade + other + kk + pCekme + amzCeviri + kurCeviri + faiz;
  const kar = gelir - amzFee - promo - toplamGider;
  const roi = toplamGider > 0 ? (kar / toplamGider) * 100 : 0;

  return { kk, pCekme, amzCeviri, kurCeviri, faiz, toplamGider, kar, roi };
}

/**
 * Kullanıcı girdisini sayıya çevirir.
 *
 * Virgülü noktaya çeviriyor, `$` ve `%` işaretlerini atıyor, boş/geçersiz
 * girdide **0** dönüyor. Excel'den yapıştırılan hücreler bu biçimlerde geliyor.
 */
export function sayiyaCevir(deger: unknown): number {
  if (deger === null || deger === undefined || deger === '') return 0;
  const temiz = String(deger)
    .replace(/,/g, '.')
    .replace(/\$/g, '')
    .replace(/%/g, '')
    .trim();
  const n = Number.parseFloat(temiz);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Oran hiyerarşisi: **önce o ayın oranı**, yoksa genel ayar, yoksa varsayılan.
 *
 * Aylık oran ayrı tutuluyor çünkü komisyonlar yıl içinde değişiyor; geçmiş bir
 * ayı bugünün oranıyla hesaplamak o ayın kârını değiştirirdi.
 */
export function oranlariCoz(
  aylikOran: Partial<KarOranlari> | null | undefined,
  genelAyar: Partial<KarOranlari> | null | undefined,
): KarOranlari {
  const kaynak = aylikOran ?? genelAyar ?? {};
  return {
    kkRate: kaynak.kkRate ?? VARSAYILAN_ORANLAR.kkRate,
    pCekmeRate: kaynak.pCekmeRate ?? VARSAYILAN_ORANLAR.pCekmeRate,
    amzCeviriRate: kaynak.amzCeviriRate ?? VARSAYILAN_ORANLAR.amzCeviriRate,
    kurCeviriRate: kaynak.kurCeviriRate ?? VARSAYILAN_ORANLAR.kurCeviriRate,
    faizRate: kaynak.faizRate ?? VARSAYILAN_ORANLAR.faizRate,
  };
}

/** Aylık sabit giderler — satır bazlı değil, ay bazlı. */
export const SABIT_GIDER_ALANLARI = [
  'primeFee',
  'otherFixed',
  'softwareFee',
  'serverFee',
  'companyFee',
  'amzSubFee',
  'depoKrediKom',
  'iadeFixedFee',
] as const;

export type SabitGiderAlani = (typeof SABIT_GIDER_ALANLARI)[number];

export const SABIT_GIDER_ETIKETLERI: Record<SabitGiderAlani, string> = {
  primeFee: 'Prime ücreti',
  otherFixed: 'Diğer sabit',
  softwareFee: 'Yazılım',
  serverFee: 'Sunucu',
  companyFee: 'Şirket',
  amzSubFee: 'Amazon aboneliği',
  depoKrediKom: 'Depo kredi komisyonu',
  iadeFixedFee: 'İade sabit',
};

/** Gider dökümündeki satır adları — ekranda tablo başlığı olarak kullanılıyor. */
export const GIDER_DOKUM_ETIKETLERI = {
  amazonFee: 'Amazon komisyonu',
  promosyon: 'Promosyon',
  buyFee: 'Alış bedeli',
  depoFee: 'Depo',
  iadeFee: 'İade',
  otherFee: 'Diğer',
  kkKomisyon: 'Kredi kartı komisyonu',
  pCekmeKomisyon: 'Para çekme',
  amzCeviriKomisyon: 'Amazon çeviri',
  kurCeviriKomisyon: 'Kur çeviri',
  faizGideri: 'Faiz gideri',
} as const;

export const AYLAR = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
] as const;

export type AyAdi = (typeof AYLAR)[number];
